import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { $recipientMandatoryFieldsStore } from '@lib/store';
import { cn, PaletteUtils, ValidateUtils } from '@shared/utils';
import type { IFormDataBusinessBetween } from '@pages/paymentBetweenAccounts/widgets';
import type { IFormDataBusinessOther } from '@pages/paymentOtherBank/widgets';
import type { IFormDataBusinessCreate } from '@pages/recipientEditor/widgets';
import { BaseInput, ErrorMessage, Text } from '@shared/widgets';

export type CompanyFormDataType = IFormDataBusinessCreate & IFormDataBusinessBetween & IFormDataBusinessOther;

interface CompanyFormBlockProps {
  className?: string;
}

export const CompanyFormBlock = (props: CompanyFormBlockProps) => {
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);

  const { control } = useFormContext<CompanyFormDataType>();

  const { t } = useTranslation('payments');

  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('form.titles.companyDetails')}</Text>
      <Controller
        name='companyName'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: { value: ValidateUtils.alphaNumericRegex, message: t('errors.form.alphaNumericFormat', { ns: 'common', field: 'Company Name' }) },
        }}
        render={({ field, fieldState: { error } }) => (
          <div>
            <BaseInput {...field} label={t('form.placeholders.companyName')} placeholder={t('form.placeholders.companyName')} required />
            <ErrorMessage text={error?.message} />
          </div>
        )}
      />
      {mandatoryFields.data.includes('insi_email') && (
        <Controller
          name='email'
          control={control}
          rules={{
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput
                {...field}
                type='email'
                label={t('placeholders.email', { ns: 'common' })}
                placeholder={t('placeholders.email', { ns: 'common' })}
              />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
      )}
    </div>
  );
};
